import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'effector-react';
import spam, {
  clearPrefetchedPage,
  DEFAULT_SPAM_FILTERS,
  getList,
  isCurrentPrefetchRequest,
  prefetchPage,
  resetFastPrefetch,
  showPrefetchedPage,
} from '@stores/spam';
import PostCard from '@components/PostCard';
import { Spin, Pagination, Empty } from 'antd';
import css from './SpamList.module.less';
import { pageSizeOptions } from '@constants/pagination';
import { nanoid } from 'nanoid';
import useScrollTopPage from '@hooks/useScrollTopPage';
import localeWeight, { LOCALE } from '@stores/localeWeight';

interface SpamListProps {}

const SpamList: FunctionComponent<SpamListProps> = () => {
  const {
    list,
    pagination,
    preloaders,
    filters,
    prefetchedPage,
    prefetchedPageRequest,
    shouldPrefetchNextPageFast,
    setPagination,
  } = useStore(spam);
  const locale = useStore(localeWeight);
  const [paginationKey, setPaginationKey] = useState(nanoid());
  const [isApplyingPrefetchedPage, setIsApplyingPrefetchedPage] = useState(false);
  const [waitingPrefetchedPage, setWaitingPrefetchedPage] = useState<number | null>(null);
  const prefetchTimeoutRef = useRef<number | null>(null);
  const applyTimeoutRef = useRef<number | null>(null);

  const totalPages =
    pagination.total && pagination.pageSize
      ? Math.ceil(pagination.total / pagination.pageSize)
      : 0;
  const nextPage =
    (pagination.current || 1) < totalPages ? (pagination.current || 1) + 1 : null;
  const currentCacheKey = useMemo(
    () =>
      JSON.stringify({
        ...DEFAULT_SPAM_FILTERS,
        ...filters,
        locale: locale === LOCALE.ALL ? undefined : locale,
        pageSize: pagination.pageSize,
      }),
    [filters, locale, pagination.pageSize],
  );
  const prefetchDelay = shouldPrefetchNextPageFast ? 300 : 5000;

  useEffect(() => {
    if (prefetchTimeoutRef.current) window.clearTimeout(prefetchTimeoutRef.current);
    if (waitingPrefetchedPage || prefetchedPageRequest?.isLoading) return;
    if (!nextPage) {
      resetFastPrefetch();
      return;
    }
    if (
      prefetchedPage?.page === nextPage &&
      prefetchedPage.pageSize === pagination.pageSize &&
      prefetchedPage.cacheKey === currentCacheKey &&
      prefetchedPage.items.length > Math.ceil((pagination.pageSize || 0) / 2)
    ) {
      resetFastPrefetch();
      return;
    }

    prefetchTimeoutRef.current = window.setTimeout(() => prefetchPage(nextPage), prefetchDelay);
    return () => {
      if (prefetchTimeoutRef.current) window.clearTimeout(prefetchTimeoutRef.current);
    };
  }, [
    currentCacheKey,
    nextPage,
    pagination.pageSize,
    prefetchedPage,
    prefetchedPageRequest,
    prefetchDelay,
    waitingPrefetchedPage,
  ]);

  useEffect(
    () => () => {
      if (applyTimeoutRef.current) window.clearTimeout(applyTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!waitingPrefetchedPage) return;
    if (
      prefetchedPage?.page === waitingPrefetchedPage &&
      prefetchedPage.pageSize === pagination.pageSize &&
      prefetchedPage.cacheKey === currentCacheKey
    ) {
      showPrefetchedPage(prefetchedPage.items);
      clearPrefetchedPage();
      setWaitingPrefetchedPage(null);
      applyTimeoutRef.current = window.setTimeout(() => setIsApplyingPrefetchedPage(false), 200);
    } else if (
      prefetchedPageRequest &&
      !prefetchedPageRequest.isLoading &&
      prefetchedPageRequest.page === waitingPrefetchedPage
    ) {
      setWaitingPrefetchedPage(null);
      setIsApplyingPrefetchedPage(false);
      clearPrefetchedPage();
      getList();
    }
  }, [
    currentCacheKey,
    pagination.pageSize,
    prefetchedPage,
    prefetchedPageRequest,
    waitingPrefetchedPage,
  ]);

  const paginationChange = (newCurrent: number, pageSize?: number) => {
    const notMatchedPosts = list.filter((p) => {
      if (!filters.needChecked) return !!p.isChecked;
      return false;
    });
    const isForward = newCurrent > (pagination.current || 0);

    let current = newCurrent;
    if (notMatchedPosts.length && isForward) {
      current = newCurrent - 1;
      setPaginationKey(nanoid());
    }
    const nextPageSize = pageSize || pagination.pageSize;

    localStorage.setItem('spamPageSize', `${nextPageSize}`);

    const applyCachedPage = (items: typeof list, total: number) => {
      setIsApplyingPrefetchedPage(true);
      setWaitingPrefetchedPage(null);
      setPagination({ ...pagination, current, pageSize: nextPageSize, total });
      return items;
    };

    if (
      prefetchedPage?.page === current &&
      prefetchedPage.pageSize === nextPageSize &&
      prefetchedPage.cacheKey === currentCacheKey
    ) {
      showPrefetchedPage(applyCachedPage(prefetchedPage.items, prefetchedPage.total));
      clearPrefetchedPage();
      applyTimeoutRef.current = window.setTimeout(() => setIsApplyingPrefetchedPage(false), 200);
      return;
    }

    if (isCurrentPrefetchRequest(prefetchedPageRequest, current, nextPageSize, currentCacheKey)) {
      setIsApplyingPrefetchedPage(true);
      setWaitingPrefetchedPage(current);
      setPagination({ ...pagination, current, pageSize: nextPageSize });
      return;
    }

    setIsApplyingPrefetchedPage(false);
    clearPrefetchedPage();
    setPagination({ ...pagination, current, pageSize: nextPageSize });
    getList({ pagination: { current, pageSize: nextPageSize } });
  };

  useScrollTopPage(pagination);

  const isListLoading = useMemo(() => {
    const { getList, getById } = preloaders;
    return !!(getList || getById);
  }, [preloaders]);

  if (!list.length && !isListLoading) {
    return <Empty />;
  }

  return (
    <div>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          key={paginationKey}
          showSizeChanger
          showQuickJumper
          pageSizeOptions={pageSizeOptions}
          onChange={paginationChange}
        />
      </div>
      <Spin spinning={isListLoading || isApplyingPrefetchedPage}>
        <div className={css.root}>
          {list.map((post) => (
            <PostCard
              disableModerationPanel={!!preloaders.approve}
              post={post}
              key={post.id}
              mode='spam'
              isHidingPost={preloaders.hidePost === `hidePost_${post.id}`}
              isUnhidingPost={preloaders.unhidePost === `unhidePost_${post.id}`}
              isBlocking={preloaders.block === `block_${post.id}`}
              isUnblocking={preloaders.unblock === `unblock_${post.id}`}
            />
          ))}
        </div>
      </Spin>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          key={paginationKey}
          showSizeChanger
          showQuickJumper
          pageSizeOptions={pageSizeOptions}
          onChange={paginationChange}
        />
      </div>
    </div>
  );
};

export default SpamList;
